import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const LINES = [
  { text: "UNLEASH", outline: false },
  { text: "PEAK", outline: true },
  { text: "PERFORMANCE", outline: false },
];

export default function Hero() {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative flex min-h-screen items-end overflow-hidden"
    >
      <motion.div
        aria-hidden
        style={reduced ? undefined : { y, scale }}
        className="absolute inset-0"
      >
        <img
          src="/images/hero-wideshot.webp"
          alt=""
          className="h-full w-full object-cover object-[50%_65%] saturate-[1.25] contrast-[1.05]"
        />
      </motion.div>
      <div className="absolute inset-0 bg-ink/40" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-ink/40 [mask-image:linear-gradient(to_top,black,transparent)]" />

      <motion.div
        style={reduced ? undefined : { opacity: fade }}
        className="relative z-10 mx-auto w-full max-w-[1600px] px-5 pb-16 pt-32 sm:px-8 sm:pb-20"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mb-6 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-bone/80 sm:text-sm"
        >
          <span className="h-px w-10 bg-steel" />
          Dreamworlds Garage — Dunnellon, FL
        </motion.p>

        <h1 className="font-display font-black uppercase leading-[0.95] tracking-[0.02em]">
          {LINES.map((line, i) => (
            <span key={line.text} className="block overflow-hidden">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{
                  duration: 1,
                  delay: 0.2 + i * 0.14,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className={`block text-[clamp(3.8rem,13vw,11.5rem)] ${
                  line.outline ? "text-outline" : "text-bone"
                }`}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-8 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-xl"
          >
            <p className="font-display text-2xl font-semibold uppercase tracking-wide text-bone sm:text-3xl">
              Family owned and operated.
            </p>
            <p className="mt-3 text-base leading-relaxed text-bone/85 sm:text-lg">
              Specialized in Mopar | 25+ years Master Certified Chrysler Dodge
              Jeep Ram Tech. Let us know your dream and budget, we&rsquo;ll
              handle the rest!
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href="#contact"
                data-testid="hero-schedule-button"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToId("#contact");
                }}
                className="bg-navy px-8 py-4 font-display text-xl font-bold uppercase tracking-wide text-white transition-colors duration-200 hover:bg-bone hover:text-ink"
              >
                Schedule Service
              </a>
              <a
                href="#services"
                data-testid="hero-services-button"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToId("#services");
                }}
                className="border border-bone/30 px-8 py-4 font-display text-xl font-bold uppercase tracking-wide text-bone transition-colors duration-200 hover:border-bone"
              >
                What We Do
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.95 }}
            className="hidden shrink-0 border-l-2 border-steel pl-6 md:block"
          >
            <p className="font-display text-7xl font-black leading-none text-bone">
              25+
            </p>
            <p className="mt-2 max-w-[180px] text-xs font-semibold uppercase tracking-[0.2em] text-bone/70">
              Years Master Certified
            </p>
          </motion.div>
        </div>
      </motion.div>

      <motion.button
        aria-label="Scroll down"
        data-testid="hero-scroll-button"
        onClick={() => scrollToId("#about")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="absolute bottom-6 right-6 z-10 hidden text-bone/70 transition-colors hover:text-bone sm:block"
      >
        <ArrowDown className="h-6 w-6 animate-bounce" />
      </motion.button>
    </section>
  );
}
